import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Comparison = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const connections = [
    {
      type: 'Fiber Internet',
      icon: FiIcons.FiWifi,
      speed: 'Up to 10 Gbps',
      reliability: '99.9%',
      latency: '1-2 ms',
      bestFor: 'Large businesses, data centers',
      cost: '$$$$',
      available: 'Limited areas',
      pros: ['Fastest speeds', 'Symmetrical upload/download', 'Most reliable'],
      cons: ['Limited availability', 'Higher cost', 'Longer installation']
    },
    {
      type: 'Cable Internet',
      icon: FiIcons.FiRadio,
      speed: 'Up to 1 Gbps',
      reliability: '99.5%',
      latency: '10-25 ms',
      bestFor: 'Most businesses, general use',
      cost: '$$$',
      available: 'Widely available',
      pros: ['Good speed-to-cost ratio', 'Wide availability', 'Quick installation'],
      cons: ['Shared bandwidth', 'Slower upload speeds', 'Speed can vary']
    },
    {
      type: 'Fixed Wireless',
      icon: FiIcons.FiSend,
      speed: 'Up to 100 Mbps',
      reliability: '99.0%',
      latency: '25-50 ms',
      bestFor: 'Rural areas, backup connection',
      cost: '$$',
      available: 'Line of sight required',
      pros: ['Available in rural areas', 'Quick setup', 'No cables needed'],
      cons: ['Weather dependent', 'Limited speeds', 'Line of sight required']
    }
  ];

  return (
    <section id="comparison" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Compare Business{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Internet Options
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Understand the differences between fiber, cable, and fixed wireless to make the best choice
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {connections.map((connection, index) => (
            <motion.div
              key={connection.type}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                  <SafeIcon icon={connection.icon} className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{connection.type}</h3>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Speed:</span>
                  <span className="font-semibold">{connection.speed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reliability:</span>
                  <span className="font-semibold">{connection.reliability}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Latency:</span>
                  <span className="font-semibold">{connection.latency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Best For:</span>
                  <span className="font-semibold text-sm">{connection.bestFor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cost:</span>
                  <span className="font-semibold">{connection.cost}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Available:</span>
                  <span className="font-semibold text-sm">{connection.available}</span>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="mb-3">
                  <h4 className="font-semibold text-green-600 mb-1">Pros:</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {connection.pros.map((pro, proIndex) => (
                      <li key={proIndex} className="flex items-start gap-2">
                        <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-red-600 mb-1">Cons:</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {connection.cons.map((con, conIndex) => (
                      <li key={conIndex} className="flex items-start gap-2">
                        <SafeIcon icon={FiIcons.FiX} className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Need help choosing the right connection?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our experts will analyze your business needs and recommend the best option at the best price
          </p>
          <button
            onClick={() => scrollToSection('contact')}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Compare All Providers
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Comparison;